'use client';

import React, { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { client } from '@ias/graphql-sdk';
import ContentModule from '../ContentModule';
import { useSession, signIn } from 'next-auth/react';

import { useAuthContext } from '../AuthProvider';
import { ArticleLoading } from '../ArticleLoading';
import { localizeHref } from '../../utils/localizeHref';

export const RESELLER_GROUP = 'IAS Product Reseller';
const IS_SIGNAL_3 = process.env.SITE === 'IAS-KB-SIGNAL3-SITE';

type PageParams = {
  path: string;
  locale: string;
  preview: boolean;
  site?: string;
  title?: string;
  auth?: string;
  ssoGroupItems?: Array<string>;
  contentTypeId?: string;
};

// Module declaration moved to src/types/next-auth.d.ts

function AuthGuardContent({ params, groups }: { params: PageParams; groups?: string[] }) {
  const [pageData, setPageData] = useState<any>(null);
  const [loaded, setLoaded] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const loadPageData = async () => {
      try {
        const { data } = await client.PageV2({ ...params, groups });
        setLoaded(true);
        setPageData(data);
      } catch {
        router.push(localizeHref('/404', params?.locale));
      }
    };
    loadPageData();
  }, [params]);

  if (!loaded) {
    return <ArticleLoading />;
  }

  if (pageData) {
    return (
      <>
        <ContentModule {...pageData.pageV2} />
        {!!pageData.pageV2?.globalChatBot && <ContentModule {...pageData.pageV2.globalChatBot} />}
      </>
    );
  }

  return null;
}

export function AuthGuard({
  params: { auth, ...params },
  requiredRoles
}: {
  params: PageParams;
  requiredRoles?: string[];
}) {
  const router = useRouter();
  // Call hooks unconditionally at the top level
  const authContext = useAuthContext();
  const { setIsRedirecting } = authContext;

  if (auth === 'Okta') {
    const [initialized, setInitialized] = useState(false);
    const session = useSession();
    const status = session?.status;
    useEffect(() => {
      if (status === 'unauthenticated') {
        // console.log('[AuthGuard] Okta: User is unauthenticated - redirecting to login');
        // Notify context that redirect is happening
        setIsRedirecting(true);
        signIn('okta');
      }
    }, [auth, session, setIsRedirecting]);
    useEffect(() => {
      if (!session || session?.status !== 'authenticated') return;
      if (!params) return;
      const sessionGroups = session?.data?.groups ?? [];
      const hasRestrictedGroup = sessionGroups.includes(RESELLER_GROUP);
      const articleGroups = params?.ssoGroupItems ?? [];
      const isArticleForReseller = articleGroups.includes(RESELLER_GROUP);
      if (
        hasRestrictedGroup &&
        !isArticleForReseller &&
        params?.path !== '/' &&
        params?.path !== '/search' &&
        params?.contentTypeId !== 'categoryArticle'
      ) {
        router.push(localizeHref('/', params?.locale));
      } else {
        setInitialized(true);
      }
    }, [session, params]);
    if (status === 'authenticated' && initialized) {
      return <AuthGuardContent params={params} groups={session?.data?.groups ?? []} />;
    }
  } else {
    const { authenticated, initializing, apps } = authContext;
    const pathname = usePathname();
    useEffect(() => {
      if (!initializing && !authenticated && auth !== 'Okta') {
        // console.log('[AuthGuard] Non-Okta: User is unauthenticated - redirecting to login');
        // console.log('[AuthGuard] Current state:', { initializing, authenticated, pathname });
        // Notify context that redirect is happening
        setIsRedirecting(true);
        const redirectUrl = `${process.env.USER_AUTH_DOMAIN}?redirectUri=${encodeURIComponent(
          `${window.location.origin}${pathname}`
        )}`;
        // console.log('[AuthGuard] Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
      }
    }, [initializing, pathname, authenticated, auth, setIsRedirecting]);

    if (authenticated && IS_SIGNAL_3) {
      const hasAccess = !!requiredRoles?.filter((role: string) => apps?.includes(role)).length;
      if (hasAccess) {
        return <AuthGuardContent params={params} />;
      } else {
        router.push(localizeHref('/404', params?.locale));
      }
    } else if (authenticated) {
      return <AuthGuardContent params={params} />;
    }
  }

  return <ArticleLoading />;
}
